/* =============================================================================
 * Header, drawer, mega menu, announcement bar, footer accordions.
 * v1.0 §8.1, §8.2, §8.16 · Part II §N.
 *
 * Vanilla ES2022, deferred, no framework (§P.5). Custom elements are defined
 * through window.Eloss.define, which wraps each constructor in try/catch so one
 * failing element can never take the page down (§P.5).
 *
 * Progressive enhancement is mandatory (§P.5): every control here is already a
 * working link, button or form before this file runs. With JavaScript off the
 * nav, search, cart, footer and policies all remain reachable.
 * ========================================================================== */

(() => {
  const Eloss = window.Eloss;
  if (!Eloss) return;

  const MEGA_INTENT_DELAY = 120; // v1.0 §8.2 — pointer only, never keyboard.
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const focusable = (root) =>
    Array.from(root.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement
    );

  /* ---------------------------------------------------------------------------
   * Header: sticky behaviour, transparent mode, mega menu, mobile drawer.
   * ------------------------------------------------------------------------ */
  Eloss.define('eloss-header', class ElossHeader extends HTMLElement {
    connectedCallback() {
      this.stickyMode = this.dataset.sticky || 'never';
      this.isTransparent = this.dataset.transparent === 'true';
      this.lastY = window.scrollY;
      this.intentTimer = null;

      this.initMega();
      this.initDrawer();

      if (this.stickyMode === 'scroll-up' || this.isTransparent) {
        this.onScroll = Eloss.debounce(() => this.handleScroll(), 16);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.handleScroll();
      }
    }

    disconnectedCallback() {
      /* Every listener attached OUTSIDE this element must be removed here, or
         it outlives the element. The Theme Editor disconnects and reconnects
         sections on each change, so anything missed accumulates. */
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.onDocClick) document.removeEventListener('click', this.onDocClick);
      clearTimeout(this.intentTimer);
      this.closeMega();
      this.closeDrawer();
    }

    /* --- Sticky + transparent. Class toggles only; never an inline `top`. -- */
    handleScroll() {
      const y = window.scrollY;

      if (this.isTransparent) {
        this.classList.toggle('header-wrapper--solid', y > 80);
      }

      if (this.stickyMode === 'scroll-up') {
        const goingDown = y > this.lastY && y > 120;
        this.classList.toggle('header-wrapper--hidden', goingDown && !this.megaOpen);
      }

      this.lastY = y;
    }

    /* --- Mega menu (v1.0 §8.2, §N.2) -------------------------------------- */
    initMega() {
      this.megaTrigger = this.querySelector('[data-mega-trigger]');
      this.megaPanel = this.querySelector('[data-mega-panel]');
      if (!this.megaTrigger || !this.megaPanel) return;

      const item = this.megaTrigger.closest('.header__nav-item');

      /* Click and keyboard: instant. §N.2 — the intent delay must never apply
         to keyboard, and must never trap focus. */
      this.megaTrigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.megaOpen ? this.closeMega() : this.openMega();
      });

      /* Pointer only: 120 ms intent delay. */
      if (item && window.matchMedia('(hover: hover)').matches) {
        item.addEventListener('mouseenter', () => {
          clearTimeout(this.intentTimer);
          this.intentTimer = setTimeout(() => this.openMega(), MEGA_INTENT_DELAY);
        });
        item.addEventListener('mouseleave', () => {
          clearTimeout(this.intentTimer);
          this.closeMega();
        });
      }

      /* Esc closes and restores focus to the trigger (§N.2). */
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.megaOpen) {
          this.closeMega();
          this.megaTrigger.focus();
        }
      });

      /* Tab out of the panel closes it (§N.2). */
      this.megaPanel.addEventListener('focusout', (e) => {
        if (!this.megaPanel.contains(e.relatedTarget) && e.relatedTarget !== this.megaTrigger) {
          this.closeMega();
        }
      });

      /* Outside click closes. */
      this.onDocClick = (e) => {
        if (this.megaOpen && !this.contains(e.target)) this.closeMega();
      };
      document.addEventListener('click', this.onDocClick);
    }

    openMega() {
      if (!this.megaPanel || this.megaOpen) return;
      this.megaOpen = true;
      this.megaPanel.hidden = false;
      this.megaTrigger.setAttribute('aria-expanded', 'true');
    }

    closeMega() {
      if (!this.megaPanel || !this.megaOpen) return;
      this.megaOpen = false;
      this.megaPanel.hidden = true;
      this.megaTrigger.setAttribute('aria-expanded', 'false');
    }

    /* --- Mobile drawer (§N.1: inert PLUS focus trap and restoration) ------- */
    initDrawer() {
      this.drawerOpener = this.querySelector('[data-drawer-open]');
      this.drawer = this.querySelector('[data-header-drawer]');
      if (!this.drawerOpener || !this.drawer) return;

      this.overlay = document.createElement('div');
      this.overlay.className = 'header-drawer-overlay';
      this.overlay.hidden = true;
      this.drawer.after(this.overlay);

      this.drawerOpener.addEventListener('click', () => this.openDrawer());
      this.overlay.addEventListener('click', () => this.closeDrawer());
      this.drawer.querySelector('[data-drawer-close]')?.addEventListener('click', () => this.closeDrawer());

      this.drawer.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          this.closeDrawer();
        } else if (e.key === 'Tab') {
          this.trapFocus(e);
        }
      });

      /* Submenu disclosures are real buttons (v1.0 §17). */
      this.drawer.querySelectorAll('.header-drawer__link--toggle').forEach((btn) => {
        btn.addEventListener('click', () => {
          const panel = this.drawer.querySelector(`#${CSS.escape(btn.getAttribute('aria-controls'))}`);
          if (!panel) return;
          const open = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-expanded', String(!open));
          panel.hidden = open;
        });
      });
    }

    openDrawer() {
      if (this.drawerOpen) return;
      this.drawerOpen = true;
      this.returnFocusTo = document.activeElement;

      this.drawer.hidden = false;
      this.overlay.hidden = false;
      this.drawerOpener.setAttribute('aria-expanded', 'true');
      document.documentElement.style.overflow = 'hidden';

      /* §N.1: inert the background, but the focus trap below is the guarantee. */
      this.inerted = Array.from(document.body.children).filter(
        (el) => el !== this.drawer && !el.contains(this.drawer)
      );
      this.inerted.forEach((el) => {
        el.inert = true;
        el.setAttribute('aria-hidden', 'true');
      });

      focusable(this.drawer)[0]?.focus();
    }

    closeDrawer() {
      if (!this.drawerOpen) return;
      this.drawerOpen = false;

      this.drawer.hidden = true;
      this.overlay.hidden = true;
      this.drawerOpener.setAttribute('aria-expanded', 'false');
      document.documentElement.style.overflow = '';

      this.inerted?.forEach((el) => {
        el.inert = false;
        el.removeAttribute('aria-hidden');
      });
      this.inerted = null;

      /* Focus restoration (§N.1).
         document.body is REJECTED as a restore target, not merely null-checked.
         Safari does not focus a <button> when it is clicked, so activeElement at
         open time is <body> there — and <body> is an HTMLElement, so the old
         instanceof test accepted it and focus was dropped to the top of the
         document on every Safari close. Verified on this theme: opening with the
         opener unfocused left focus on BODY. Same guard component-facets.js uses. */
      const restoreTo =
        this.returnFocusTo instanceof HTMLElement &&
        this.returnFocusTo !== document.body &&
        document.contains(this.returnFocusTo)
          ? this.returnFocusTo
          : this.drawerOpener;
      restoreTo?.focus();
      this.returnFocusTo = null;
    }

    trapFocus(e) {
      const items = focusable(this.drawer);
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });
})();
